<script lang="ts">
export default {
  name: 'CoverageTypeDeleteModal',
};
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import { PolicyType } from '@/types/PolicyType';
import { policyTypeService } from '@/services/PolicyTypeService';

const props = defineProps<{
  show: boolean;
  coverageType: string;
  policyTypes: PolicyType[];
}>();

const emit = defineEmits<{
  close: [];
  deleted: [];
}>();

const loading = ref(false);
const error = ref('');

// Calculate usage from existing policy types data
const usageCount = computed(() => {
  return props.policyTypes.filter((pt) => pt.coverageType === props.coverageType).length;
});

const canDelete = computed(() => usageCount.value === 0);

const usageMessage = computed(() => {
  if (usageCount.value === 0) {
    return `Are you sure you want to delete the coverage type "${props.coverageType}"? This action cannot be undone.`;
  } else {
    return `Cannot delete coverage type "${props.coverageType}" because it is currently mapped to ${usageCount.value} policy type${usageCount.value > 1 ? 's' : ''}. Please unmap all policy types before deleting.`;
  }
});

const actionButtonLabel = computed(() => {
  return canDelete.value ? 'Delete' : 'Close';
});

async function handleAction() {
  if (!canDelete.value) {
    handleCancel();
    return;
  }

  loading.value = true;
  error.value = '';

  try {
    await policyTypeService.DeleteCoverageType(props.coverageType);
    emit('deleted');
    handleCancel();
  } catch (err: any) {
    console.error('Error deleting coverage type:', err);
    error.value = err.response?.data?.message || err.message || 'Failed to delete coverage type. Please try again.';
  } finally {
    loading.value = false;
  }
}

function handleCancel() {
  error.value = '';
  emit('close');
}

// Reset error when modal is closed/opened
watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      error.value = '';
    }
  },
);
</script>

<template>
  <AppModal
    :show="show"
    title="Delete Coverage Type"
    :action-button-label="actionButtonLabel"
    cancel-button-label="Cancel"
    :handle-action="handleAction"
    :handle-cancel="handleCancel"
    :loading="loading"
    :action-disabled="false"
    size="md">
    <template #content>
      <div class="tw:space-y-4">
        <div class="tw:text-gray-700">
          {{ usageMessage }}
        </div>

        <div
          v-if="!canDelete && usageCount > 0"
          class="tw:rounded-md tw:border tw:border-yellow-200 tw:bg-yellow-50 tw:p-3">
          <div class="tw:flex">
            <div class="tw:shrink-0">
              <svg class="tw:h-5 tw:w-5 tw:text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clip-rule="evenodd" />
              </svg>
            </div>
            <div class="tw:ml-3">
              <h3 class="tw:text-sm tw:font-medium tw:text-yellow-800">Coverage Type in Use</h3>
              <div class="tw:mt-1 tw:text-sm tw:text-yellow-700">
                To delete this coverage type, first change the coverage type mapping for all associated policy types.
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:p-3">
          <div class="tw:flex">
            <div class="tw:shrink-0">
              <svg class="tw:h-5 tw:w-5 tw:text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd" />
              </svg>
            </div>
            <div class="tw:ml-3">
              <h3 class="tw:text-sm tw:font-medium tw:text-red-800">Error</h3>
              <div class="tw:mt-1 tw:text-sm tw:text-red-700">
                {{ error }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </AppModal>
</template>
